import { lazy, Suspense, useMemo, type ComponentType, type JSX } from 'react';
import type { MDXComponents } from 'mdx/types';
import { useParams } from 'react-router';
import { NotFoundPage } from '../not-found/not-found-page.js';
import { Skeleton } from '../../components/ui/skeleton/skeleton.js';
import { getPostMeta, loadPostComponent } from './manifest.js';
import { mdxComponents } from './mdx-components.js';
import { ArticleLayout } from './article-layout.js';
import { BlogPostHead } from './blog-post-head.js';

type MdxComponent = ComponentType<{ components?: MDXComponents }>;

export function BlogPostPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const meta = getPostMeta(slug);
  const loader = slug ? loadPostComponent(slug) : undefined;

  const Lazy = useMemo(
    () =>
      loader ? lazy(() => loader().then((m) => ({ default: m.default as MdxComponent }))) : null,
    // The loader is a stable per-slug reference from the manifest map.
    [slug],
  );

  if (!meta || !Lazy) {
    return <NotFoundPage />;
  }

  return (
    <>
      <BlogPostHead meta={meta} />
      <ArticleLayout meta={meta}>
        <Suspense fallback={<ArticleSkeleton />}>
          <Lazy components={mdxComponents} />
        </Suspense>
      </ArticleLayout>
    </>
  );
}

function ArticleSkeleton(): JSX.Element {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading article">
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="mt-6 h-64 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-4/5" />
    </div>
  );
}
